import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useLang } from "../lib/i18n";
import { useAppData } from "../context/AppDataContext";

export const SORTS = ["newest", "price_asc", "price_desc"];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-navy/70">{label}</span>
      {children}
    </label>
  );
}

export default function FilterSheet({ open, onOpenChange, filters, onApply, autoFocusKeyword }) {
  const { t, lang } = useLang();
  const { categories, zones, zoneName, guardCategory } = useAppData();
  const [draft, setDraft] = useState(filters);
  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="mx-auto max-h-[88dvh] max-w-xl overflow-y-auto rounded-t-[28px] border-0 px-5 pb-safe-6 pt-6"
        data-no-swipe
        data-testid="filter-sheet"
      >
        <div className="mx-auto -mt-3 mb-3 h-1 w-10 rounded-full bg-slate-200" />
        <SheetTitle className="text-xl font-extrabold text-navy">{t("mercat.filters")}</SheetTitle>
        <SheetDescription className="sr-only">{t("mercat.filters")}</SheetDescription>
        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onApply(draft);
          }}
        >
          <Field label={t("filters.keyword")}>
            <input
              className="field"
              value={draft.q}
              autoFocus={autoFocusKeyword}
              placeholder={t("filters.keywordPh")}
              onChange={(e) => set("q")(e.target.value)}
              data-testid="filter-keyword-input"
            />
          </Field>
          <Field label={t("filters.category")}>
            <Select value={draft.category || "any"} onValueChange={(v) => {
              const c = v === "any" ? "" : v;
              guardCategory(c, () => setDraft((d) => ({ ...d, category: c, looking_for: c === "contactos" ? "amistad" : "" })));
            }}>
              <SelectTrigger className="field" data-testid="filter-category-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="any">{t("filters.allCategories")}</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.key} value={c.key} data-testid={`filter-category-option-${c.key}`}>
                    {lang === "en" ? c.name_en : c.name_es}{c.adult ? " (+18)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("filters.zone")}>
            <Select value={draft.zone} onValueChange={set("zone")}>
              <SelectTrigger className="field" data-testid="filter-zone-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {zones.map((z) => (
                  <SelectItem key={z.key} value={z.key} data-testid={`filter-zone-option-${z.key}`}>{zoneName(z.key)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("filters.priceMin")}>
              <input className="field" type="number" min="0" inputMode="decimal" value={draft.price_min}
                onChange={(e) => set("price_min")(e.target.value)} data-testid="filter-price-min-input" />
            </Field>
            <Field label={t("filters.priceMax")}>
              <input className="field" type="number" min="0" inputMode="decimal" value={draft.price_max}
                onChange={(e) => set("price_max")(e.target.value)} data-testid="filter-price-max-input" />
            </Field>
          </div>
          <Field label={t("filters.sort")}>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {SORTS.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => set("sort")(s)}
                  data-testid={`filter-sort-${s}`}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                    draft.sort === s ? "border-brand bg-brand text-white" : "border-slate-200 text-navy hover:border-brand/60"
                  }`}
                >
                  {t(`sort.${s}`)}
                </button>
              ))}
            </div>
          </Field>
          <div className="flex gap-2.5 pt-2">
            <button
              type="button"
              className="btn-outline flex-1"
              onClick={() => setDraft({ q: "", category: "", zone: "all", price_min: "", price_max: "", sort: "newest" })}
              data-testid="filter-clear-btn"
            >
              {t("filters.clear")}
            </button>
            <button type="submit" className="btn-primary flex-[2]" data-testid="filter-apply-btn">{t("filters.apply")}</button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
